import React from "react";
import { motion } from "framer-motion";
import {
  ClipboardList, Factory, PackageCheck, CalendarRange,
  ClipboardCheck, Ship, Container, CheckCircle2,
} from "lucide-react";
import { Reveal, SectionHeading, easeOut } from "./primitives";

const STEPS = [
  { icon: ClipboardList, title: "Select Product", desc: "Choose the product & specs you want to source in bulk." },
  { icon: Factory, title: "Choose Manufacturer", desc: "Pick a verified Turkish manufacturer for your order." },
  { icon: PackageCheck, title: "Confirm Volume", desc: "Lock full-container or bulk quantity & packaging." },
  { icon: CalendarRange, title: "Production Planning", desc: "Align production schedule and factory capacity." },
  { icon: ClipboardCheck, title: "Inspection", desc: "Schedule pre-shipment quality inspection." },
  { icon: Ship, title: "Freight Booking", desc: "Coordinate freight with FreightIQ™." },
  { icon: Container, title: "Shipment", desc: "Track container allocation and departure." },
  { icon: CheckCircle2, title: "Delivered", desc: "Receive goods with full documentation." },
];

export default function HowItWorks() {
  return (
    <section id="how" className="bg-[#F8FAFC] py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal>
          <SectionHeading
            align="center"
            eyebrow="How It Works"
            title="One connected procurement flow."
            subtitle="From product selection to delivery — every step runs inside a single Workspace, connected end to end."
          />
        </Reveal>

        <div className="relative mt-16">
          <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, ease: easeOut, delay: (i % 4) * 0.08 }}
                className="relative"
                data-testid={`workflow-step-${i}`}
              >
                {i !== STEPS.length - 1 && (
                  <div className="absolute left-[46px] right-0 top-6 hidden h-px lg:block" aria-hidden>
                    <svg width="100%" height="2" className="overflow-visible">
                      <line x1="0" y1="1" x2="100%" y2="1" stroke="#CBD5E1" strokeWidth="2" className="flow-line" />
                    </svg>
                  </div>
                )}
                <div className="relative flex flex-col items-start">
                  <div className="relative z-10 flex h-12 w-12 items-center justify-center rounded-xl border border-slate-200 bg-white text-[#0052FF] shadow-[0_6px_16px_-8px_rgba(10,17,40,0.25)]">
                    <s.icon className="h-5 w-5" />
                    <span className="font-mono-ui absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#0A1128] text-[10px] font-bold text-white">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="font-display mt-4 text-[15px] font-bold text-[#0A1128]">{s.title}</h3>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-[#64748B]">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
